import type {
  AutonomyRow,
  ContentKind,
  DefectVerificationKind,
  LevelId,
  Operation,
  ProgrammeCell,
  ProgrammeLevelFilter,
  ProgrammeOverview,
  ProgrammeStateAbsentResponse,
  ProgrammeStateCell,
  ProgrammeStateControls,
  ProgrammeStateDefect,
  ProgrammeStateDocument,
  ProgrammeStateGap,
  ProgrammeStateGetResponse,
  ProgrammeStateResponse,
  ProgrammeStateSource,
  ProgrammeStateStructuralRow,
  ProgrammeStateTarget,
  ProgrammeStateWorkType,
  ProgrammeCellUnit,
  Stage,
} from '../types/programme.js';

export const CANONICAL_STAGES: readonly Stage[] = [
  'targeted',
  'designed',
  'drafted',
  'gated',
  'reviewed',
  'import_ready',
  'imported',
  'in_review',
  'approved',
  'executing',
  'executed',
  'rollback_failed',
  'rolled_back',
  'cancelled',
  'refused',
  'discarded',
  'live',
] as const;

export const CONTENT_KIND_ORDER: readonly ContentKind[] = [
  'ml',
  'elpi',
  'cupi',
  'nelpi',
  'cumulative',
  'module',
  'mixed',
  'unknown',
  'none',
];

export const CONTENT_KIND_LABEL: Record<ContentKind, string> = {
  ml: 'Micro-lessons',
  elpi: 'Exam-level practice items',
  cupi: 'Concept-understanding practice items',
  nelpi: 'Non-exclusive exam-level practice items',
  cumulative: 'Cumulative practice items',
  module: 'Modules',
  mixed: 'Mixed',
  unknown: 'Unknown kind',
  none: 'None',
};

export const NEVER_PUBLISHED_COPY = 'no programme state document has been published yet';

export const COUNT_KEY_LINE =
  'no data means nothing was read; 0 means something was read and it was zero; not required means this floor does not ask for any.';

export const AUTONOMY_PRIMER_PLAIN =
  'A Clopper-Pearson interval is an exact 95% confidence interval on a binomial rate: the true refusal rate is very likely between the lower and upper bounds, and a small sample produces a wide interval.';

export const AUTONOMY_PRIMER_ARITHMETIC =
  '0 refusals in 12 decisions gives an upper bound of 26.5%, which clears none of the bars; 0 in 17 gives 19.5%, which clears 20%; 0 in 183 gives 2.0%, which clears the governing 2% bar.';

export const AUTONOMY_PRIMER_GOOD_BAD =
  'A good value is a tight interval whose upper bound sits below the 2% governing bar. A bad value is a wide interval that clears no bar, or an upper bound that stays above 2% even with a large n.';

export const STRUCTURAL_NON_BLOCKING =
  'An unapproved structural proposal does not block content drafting.';

export const FRESHNESS_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export const LABEL_NO_DATA = 'no data';
export const LABEL_ZERO = '0';
export const LABEL_NOT_REQUIRED = 'not required by this floor';
export const LABEL_TARGET_UNKNOWN = 'target unknown';
export const LABEL_NOT_MEASURED = 'not measured';
export const LABEL_INCOMPLETE_VERDICT = 'suppressed — incomplete scan';
const CONTENT_KIND_SET = new Set<string>(CONTENT_KIND_ORDER);
const OPERATION_SET = new Set<string>([
  'create',
  'repair',
  'retire',
  'rework',
  'pinpoint_fix',
  'augment',
  'hide',
  'move',
  'rename',
  'reorder',
  'archive',
  'evaluate',
  'qa',
  'other',
]);
const UNIT_SET = new Set<string>(['work_items', 'content_units', 'lessons', 'tasks', 'modules', 'designs']);

export const FALLBACK_WORK_TYPES: readonly ProgrammeStateWorkType[] = [
  { id: 'elpi_new', label: 'New ELPI', description: '', origin: 'new', contentKind: 'elpi', operation: 'create' },
  { id: 'cupi_new', label: 'New CUPI', description: '', origin: 'new', contentKind: 'cupi', operation: 'create' },
  { id: 'nelpi_new', label: 'New NELPI', description: '', origin: 'new', contentKind: 'nelpi', operation: 'create' },
  { id: 'cumulative_new', label: 'New cumulative', description: '', origin: 'new', contentKind: 'cumulative', operation: 'create' },
  { id: 'pi_repair', label: 'PI repair', description: '', origin: 'modify', contentKind: 'unknown', operation: 'repair' },
  { id: 'pi_retire', label: 'PI retire', description: '', origin: 'modify', contentKind: 'unknown', operation: 'retire' },
  { id: 'pi_augment', label: 'PI augment', description: '', origin: 'modify', contentKind: 'unknown', operation: 'augment' },
  { id: 'ml_new', label: 'New micro-lesson', description: '', origin: 'new', contentKind: 'ml', operation: 'create' },
  { id: 'ml_rework', label: 'ML rework', description: '', origin: 'modify', contentKind: 'ml', operation: 'rework' },
  { id: 'ml_pinpoint_fix', label: 'ML pinpoint fix', description: '', origin: 'modify', contentKind: 'ml', operation: 'pinpoint_fix' },
  { id: 'ml_augment_practice', label: 'ML augment practice', description: '', origin: 'modify', contentKind: 'ml', operation: 'augment' },
  { id: 'ml_retire', label: 'ML retire', description: '', origin: 'modify', contentKind: 'ml', operation: 'retire' },
  { id: 'ml_hide', label: 'ML hide', description: '', origin: 'modify', contentKind: 'ml', operation: 'hide' },
  { id: 'structural_move', label: 'Structural move', description: '', origin: 'move', contentKind: 'module', operation: 'move' },
  { id: 'structural_rename', label: 'Structural rename', description: '', origin: 'modify', contentKind: 'module', operation: 'rename' },
  { id: 'structural_reorder', label: 'Structural reorder', description: '', origin: 'modify', contentKind: 'module', operation: 'reorder' },
  { id: 'structural_archive', label: 'Structural archive', description: '', origin: 'modify', contentKind: 'module', operation: 'archive' },
  { id: 'diagram_redraw', label: 'Diagram redraw', description: '', origin: 'modify', contentKind: 'none', operation: 'other' },
  { id: 'discard_evaluation', label: 'Discard evaluation', description: '', origin: 'none', contentKind: 'none', operation: 'evaluate' },
  { id: 'qa', label: 'QA', description: '', origin: 'none', contentKind: 'none', operation: 'qa' },
  { id: 'other', label: 'Other', description: '', origin: 'none', contentKind: 'unknown', operation: 'other' },
];

export function parseProgrammeLevel(raw: string | null | undefined): ProgrammeLevelFilter {
  if (raw === 'I' || raw === 'II' || raw === 'III' || raw === 'all') {
    return raw;
  }
  return 'all';
}

export function isAbsentProgrammeState(
  response: ProgrammeStateResponse,
): response is ProgrammeStateAbsentResponse {
  return response.state === null;
}

/**
 * Three facts that must never share a glyph: unread, measured-zero, and a floor of zero.
 * `present: false` is unread (`no data`). `present: true, value: 0` is a measured zero.
 * The ≥ prefix is applied only to a present live count from an incomplete scan.
 */
export function formatMeasuredNumber(args: {
  present: boolean;
  value: number | null | undefined;
  incomplete?: boolean;
}): string {
  if (!args.present || args.value === null || args.value === undefined) {
    return LABEL_NO_DATA;
  }
  const body = String(args.value);
  return args.incomplete ? `≥${body}` : body;
}

export function formatFloor(floor: number | null | undefined): string {
  if (floor === null || floor === undefined) {
    return LABEL_TARGET_UNKNOWN;
  }
  if (floor === 0) {
    return LABEL_NOT_REQUIRED;
  }
  return String(floor);
}

export function formatRemaining(remaining: number | null, reason?: string | null): string {
  if (remaining === null) {
    return reason && reason.trim() !== '' ? `${LABEL_NOT_MEASURED} (${reason})` : LABEL_NOT_MEASURED;
  }
  return String(remaining);
}

export function formatVerification(kind: DefectVerificationKind): string {
  if (kind === 'rate_remeasured') {
    return 'verified — class rate re-measured';
  }
  if (kind === 'dated_unverified') {
    return 'dated, not verified';
  }
  return 'no verification';
}

export function formatPercent(rate: number | null | undefined): string {
  if (rate === null || rate === undefined || !Number.isFinite(rate)) {
    return LABEL_NO_DATA;
  }
  return `${(rate * 100).toFixed(1)}%`;
}

export function formatRateRange(range: [number, number] | null | undefined): string {
  if (!range) {
    return LABEL_NO_DATA;
  }
  return `[${formatPercent(range[0])}, ${formatPercent(range[1])}]`;
}

export function formatInterval(interval: { lower: number; upper: number } | null | undefined): string {
  if (!interval) {
    return LABEL_NO_DATA;
  }
  return `${formatPercent(interval.lower)} – ${formatPercent(interval.upper)}`;
}

export function formatAutonomyVerdict(
  row: Pick<AutonomyRow, 'n' | 'verdict'>,
  scanComplete: boolean,
): string {
  if (!scanComplete) {
    return LABEL_INCOMPLETE_VERDICT;
  }
  if (row.n === 0) {
    return LABEL_NO_DATA;
  }
  return row.verdict;
}

export function autonomyMetric(n: number, value: number | null | undefined, format: 'int' | 'rate' = 'int'): string {
  if (n === 0) {
    return LABEL_NO_DATA;
  }
  if (format === 'rate') {
    return formatPercent(value);
  }
  if (value === null || value === undefined) {
    return LABEL_NO_DATA;
  }
  return String(value);
}

export function partitionDocumentCells<T extends { section: string | null }>(cells: readonly T[]): {
  sectionRows: T[];
  levelTotalRows: T[];
} {
  const sectionRows: T[] = [];
  const levelTotalRows: T[] = [];
  for (const cell of cells) {
    if (cell.section === null) {
      levelTotalRows.push(cell);
    } else {
      sectionRows.push(cell);
    }
  }
  return { sectionRows, levelTotalRows };
}

export interface CountView {
  present: boolean;
  count: number | null;
  unit: string | null;
  source: string | null;
  measuredAt: string | null;
  tentative: boolean;
}

export function emptyCountView(): CountView {
  return {
    present: false,
    count: null,
    unit: null,
    source: null,
    measuredAt: null,
    tentative: false,
  };
}

export type MatrixRowKind = 'section' | 'level_total';

export interface OverviewMatrixRow {
  id: string;
  kind: MatrixRowKind;
  level: LevelId;
  workType: string;
  workTypeLabel: string;
  contentKind: ContentKind;
  operation: Operation;
  section: string | null;
  cells: Record<string, { live: CountView; document: CountView }>;
}

export interface OverviewMatrixGroup {
  level: LevelId;
  contentKind: ContentKind;
  workType: string;
  workTypeLabel: string;
  operation: Operation;
  rows: OverviewMatrixRow[];
}

export interface OverviewMatrix {
  stages: readonly Stage[];
  groups: OverviewMatrixGroup[];
}

function countViewFromLive(
  count: number,
  unit: string,
  measuredAt: string,
): CountView {
  return {
    present: true,
    count,
    unit,
    source: 'live',
    measuredAt,
    tentative: false,
  };
}

function countViewFromDocument(cell: ProgrammeStateCell): CountView {
  return {
    present: true,
    count: cell.count,
    unit: cell.unit,
    source: cell.source,
    measuredAt: cell.measuredAt,
    tentative: cell.tentative === true,
  };
}

function emptyPair(): { live: CountView; document: CountView } {
  return { live: emptyCountView(), document: emptyCountView() };
}

function workTypeLookup(workTypes: readonly ProgrammeStateWorkType[]): Map<string, ProgrammeStateWorkType> {
  const map = new Map<string, ProgrammeStateWorkType>();
  for (const row of FALLBACK_WORK_TYPES) {
    map.set(row.id, row);
  }
  for (const row of workTypes) {
    map.set(row.id, row);
  }
  return map;
}

function metaFor(id: string, lookup: Map<string, ProgrammeStateWorkType>, fallback?: {
  contentKind?: ContentKind;
  operation?: Operation;
}): ProgrammeStateWorkType {
  const found = lookup.get(id);
  if (found) {
    return found;
  }
  return {
    id,
    label: id,
    description: '',
    origin: 'none',
    contentKind: fallback?.contentKind ?? 'unknown',
    operation: fallback?.operation ?? 'other',
  };
}

export function matchesLevel<T extends { level: LevelId }>(row: T, filter: ProgrammeLevelFilter): boolean {
  return filter === 'all' || row.level === filter;
}

export function selectedLevels(
  filter: ProgrammeLevelFilter,
  available: readonly LevelId[],
): LevelId[] {
  const wanted: LevelId[] = filter === 'all' ? ['I', 'II', 'III'] : [filter];
  const have = new Set(available);
  if (have.size === 0) {
    return wanted;
  }
  return wanted.filter((level) => have.has(level) || filter !== 'all');
}

/**
 * Live occupancy and document occupancy share a cell but are never added.
 * `section: null` document cells become their own `level_total` row and are
 * never folded into the per-section body.
 */
export function buildOverviewMatrix(input: {
  liveCells: readonly ProgrammeCell[];
  liveMeasuredAt: string;
  documentCells: readonly ProgrammeStateCell[];
  workTypes: readonly ProgrammeStateWorkType[];
  levels: readonly LevelId[];
}): OverviewMatrix {
  const lookup = workTypeLookup(input.workTypes);

  const liveAgg = new Map<string, { count: number; unit: string; contentKind: ContentKind; operation: Operation }>();
  for (const cell of input.liveCells) {
    const key = `${cell.level}\t${cell.workType}\t${cell.stage}`;
    const existing = liveAgg.get(key);
    if (existing) {
      existing.count += cell.count;
    } else {
      liveAgg.set(key, {
        count: cell.count,
        unit: cell.unit,
        contentKind: cell.contentKind,
        operation: cell.operation,
      });
    }
  }

  const docByRow = new Map<string, ProgrammeStateCell>();
  for (const cell of input.documentCells) {
    const sectionKey = cell.section === null ? '' : cell.section;
    const key = `${cell.level}\t${cell.workType}\t${sectionKey}\t${cell.stage}`;
    if (!docByRow.has(key)) {
      docByRow.set(key, cell);
    }
  }

  const workTypesByLevel = new Map<LevelId, Set<string>>();
  for (const level of input.levels) {
    workTypesByLevel.set(level, new Set());
  }
  for (const cell of input.liveCells) {
    workTypesByLevel.get(cell.level)?.add(cell.workType);
  }
  for (const cell of input.documentCells) {
    workTypesByLevel.get(cell.level)?.add(cell.workType);
  }

  const orderedIds = [
    ...input.workTypes.map((row) => row.id),
    ...FALLBACK_WORK_TYPES.map((row) => row.id),
  ];

  const groups: OverviewMatrixGroup[] = [];

  for (const level of input.levels) {
    const ids = workTypesByLevel.get(level);
    if (!ids || ids.size === 0) {
      continue;
    }
    const ordered = unique([...orderedIds.filter((id) => ids.has(id)), ...ids]);
    const kindRank = (id: string) => {
      const meta = metaFor(id, lookup, liveKind(liveAgg, level, id));
      const idx = CONTENT_KIND_ORDER.indexOf(meta.contentKind);
      return idx === -1 ? CONTENT_KIND_ORDER.length : idx;
    };
    ordered.sort((a, b) => {
      const kindDelta = kindRank(a) - kindRank(b);
      if (kindDelta !== 0) {
        return kindDelta;
      }
      return a.localeCompare(b);
    });

    for (const workType of ordered) {
      const inferred = liveKind(liveAgg, level, workType);
      const meta = metaFor(workType, lookup, inferred);
      const sections = unique(
        input.documentCells
          .filter((cell) => cell.level === level && cell.workType === workType && cell.section !== null)
          .map((cell) => cell.section as string),
      ).sort();

      const rows: OverviewMatrixRow[] = [];
      for (const section of sections) {
        rows.push(
          buildRow({
            kind: 'section',
            level,
            workType,
            meta,
            section,
            liveAgg,
            liveMeasuredAt: input.liveMeasuredAt,
            docByRow,
            includeLive: false,
          }),
        );
      }
      rows.push(
        buildRow({
          kind: 'level_total',
          level,
          workType,
          meta,
          section: null,
          liveAgg,
          liveMeasuredAt: input.liveMeasuredAt,
          docByRow,
          includeLive: true,
        }),
      );

      groups.push({
        level,
        contentKind: meta.contentKind,
        workType,
        workTypeLabel: meta.label,
        operation: meta.operation,
        rows,
      });
    }
  }

  return { stages: CANONICAL_STAGES, groups };
}

function liveKind(
  liveAgg: Map<string, { count: number; unit: string; contentKind: ContentKind; operation: Operation }>,
  level: LevelId,
  workType: string,
): { contentKind: ContentKind; operation: Operation } | undefined {
  for (const stage of CANONICAL_STAGES) {
    const hit = liveAgg.get(`${level}\t${workType}\t${stage}`);
    if (hit) {
      return { contentKind: hit.contentKind, operation: hit.operation };
    }
  }
  return undefined;
}

function buildRow(args: {
  kind: MatrixRowKind;
  level: LevelId;
  workType: string;
  meta: ProgrammeStateWorkType;
  section: string | null;
  liveAgg: Map<string, { count: number; unit: string; contentKind: ContentKind; operation: Operation }>;
  liveMeasuredAt: string;
  docByRow: Map<string, ProgrammeStateCell>;
  includeLive: boolean;
}): OverviewMatrixRow {
  const cells: OverviewMatrixRow['cells'] = {};
  for (const stage of CANONICAL_STAGES) {
    const pair = emptyPair();
    if (args.includeLive) {
      const live = args.liveAgg.get(`${args.level}\t${args.workType}\t${stage}`);
      if (live) {
        pair.live = countViewFromLive(live.count, live.unit, args.liveMeasuredAt);
      }
    }
    const sectionKey = args.section === null ? '' : args.section;
    const doc = args.docByRow.get(`${args.level}\t${args.workType}\t${sectionKey}\t${stage}`);
    if (doc) {
      pair.document = countViewFromDocument(doc);
    }
    cells[stage] = pair;
  }
  const sectionLabel = args.section === null ? 'level-total' : args.section;
  return {
    id: `${args.level}:${args.workType}:${sectionLabel}`,
    kind: args.kind,
    level: args.level,
    workType: args.workType,
    workTypeLabel: args.meta.label,
    contentKind: args.meta.contentKind,
    operation: args.meta.operation,
    section: args.section,
    cells,
  };
}

function unique<T>(values: readonly T[]): T[] {
  return [...new Set(values)];
}

export interface JoinedGapRow {
  level: LevelId;
  section: string | null;
  workType: string;
  workTypeLabel: string;
  remaining: number | null;
  remainingReason: string | null;
  floor: number | null | undefined;
  full: number | null | undefined;
  tentative: number | null;
  discarded: number | null;
  note: string | null;
  basis: string;
  priority: number | null;
  unmappedRequirements: number | null | undefined;
  source: string;
  measuredAt: string;
  unit: string;
  targetSource: string | null;
  targetMeasuredAt: string | null;
}

export function joinGapsAndTargets(input: {
  gaps: readonly ProgrammeStateGap[];
  targets: readonly ProgrammeStateTarget[];
  workTypes: readonly ProgrammeStateWorkType[];
  filter: ProgrammeLevelFilter;
}): JoinedGapRow[] {
  const lookup = workTypeLookup(input.workTypes);
  const targetByKey = new Map<string, ProgrammeStateTarget>();
  for (const target of input.targets) {
    if (!matchesLevel(target, input.filter)) {
      continue;
    }
    targetByKey.set(gapKey(target.level, target.workType, target.section ?? null), target);
  }
  const seen = new Set<string>();
  const rows: JoinedGapRow[] = [];

  for (const gap of input.gaps) {
    if (!matchesLevel(gap, input.filter)) {
      continue;
    }
    const key = gapKey(gap.level, gap.workType, gap.section ?? null);
    seen.add(key);
    const target = targetByKey.get(key);
    const tentative = gap.discardCandidates ?? gap.tentativeDiscard ?? null;
    rows.push({
      level: gap.level,
      section: gap.section ?? null,
      workType: gap.workType,
      workTypeLabel: metaFor(gap.workType, lookup).label,
      remaining: gap.remaining,
      remainingReason: gap.reason ?? null,
      floor: target?.floor,
      full: target?.full ?? null,
      tentative,
      discarded: gap.discarded ?? null,
      note: gap.note ?? null,
      basis: gap.basis,
      priority: gap.priority ?? null,
      unmappedRequirements: target?.unmappedRequirements,
      source: gap.source,
      measuredAt: gap.measuredAt,
      unit: gap.unit ?? target?.unit ?? 'work_items',
      targetSource: target?.source ?? null,
      targetMeasuredAt: target?.measuredAt ?? null,
    });
  }

  for (const [key, target] of targetByKey) {
    if (seen.has(key)) {
      continue;
    }
    rows.push({
      level: target.level,
      section: target.section ?? null,
      workType: target.workType,
      workTypeLabel: metaFor(target.workType, lookup).label,
      remaining: null,
      remainingReason: 'no gap row',
      floor: target.floor,
      full: target.full,
      tentative: null,
      discarded: null,
      note: null,
      basis: target.basis,
      priority: null,
      unmappedRequirements: target.unmappedRequirements,
      source: target.source,
      measuredAt: target.measuredAt,
      unit: target.unit ?? 'work_items',
      targetSource: target.source,
      targetMeasuredAt: target.measuredAt,
    });
  }

  rows.sort((a, b) => {
    if (a.level !== b.level) {
      return a.level.localeCompare(b.level);
    }
    if (a.workType !== b.workType) {
      return a.workType.localeCompare(b.workType);
    }
    return (a.section ?? '').localeCompare(b.section ?? '');
  });
  return rows;
}

function gapKey(level: LevelId, workType: string, section: string | null): string {
  return `${level}\t${workType}\t${section ?? ''}`;
}

export function filterStructuralRows(
  rows: readonly ProgrammeStateStructuralRow[],
  filter: ProgrammeLevelFilter,
): ProgrammeStateStructuralRow[] {
  return rows.filter((row) => matchesLevel(row, filter));
}

export function filterAutonomyRows(rows: readonly AutonomyRow[], filter: ProgrammeLevelFilter): AutonomyRow[] {
  return rows.filter((row) => matchesLevel(row, filter));
}

export function filterDefects(
  rows: readonly ProgrammeStateDefect[],
  filters: { dimension: string; exposedBy: string },
): ProgrammeStateDefect[] {
  return rows.filter((row) => {
    if (filters.dimension !== 'all' && row.dimension !== filters.dimension) {
      return false;
    }
    if (filters.exposedBy !== 'all' && row.exposedBy !== filters.exposedBy) {
      return false;
    }
    return true;
  });
}

export type FreshnessKind = 'absent_document' | 'unread_source' | 'stale_source' | 'stale_live' | 'incomplete_source';

export interface FreshnessIssue {
  kind: FreshnessKind;
  name: string;
  measuredAt?: string | undefined;
}

export function isOlderThan(iso: string | null | undefined, nowMs: number, maxAgeMs = FRESHNESS_MAX_AGE_MS): boolean {
  if (!iso) {
    return false;
  }
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) {
    return false;
  }
  return nowMs - t > maxAgeMs;
}

export function collectFreshnessIssues(input: {
  nowMs: number;
  liveMeasuredAt: string | null;
  state: ProgrammeStateGetResponse | ProgrammeStateAbsentResponse | null;
  document: ProgrammeStateDocument | null;
}): FreshnessIssue[] {
  const issues: FreshnessIssue[] = [];
  if (!input.state || isAbsentProgrammeState(input.state) || !input.document) {
    issues.push({ kind: 'absent_document', name: 'programme state document' });
  } else {
    for (const source of input.document.sources) {
      if (source.status === 'unread') {
        issues.push({
          kind: 'unread_source',
          name: source.name,
          measuredAt: source.measuredAt,
        });
        continue;
      }
      if (source.complete === false) {
        issues.push({
          kind: 'incomplete_source',
          name: source.name,
          measuredAt: source.measuredAt,
        });
      }
      if (source.measuredAt && isOlderThan(source.measuredAt, input.nowMs)) {
        issues.push({
          kind: 'stale_source',
          name: source.name,
          measuredAt: source.measuredAt,
        });
      }
    }
  }
  if (input.liveMeasuredAt && isOlderThan(input.liveMeasuredAt, input.nowMs)) {
    issues.push({
      kind: 'stale_live',
      name: 'live work items',
      measuredAt: input.liveMeasuredAt,
    });
  }
  return issues;
}

export function scopeLabel(overview: ProgrammeOverview, filter: ProgrammeLevelFilter): string {
  if (filter !== 'all') {
    return `Level ${filter}`;
  }
  return overview.scope === 'granted_courses' ? 'All granted levels' : 'All levels';
}

export function readProgrammeStateDocument(state: Record<string, unknown>): ProgrammeStateDocument {
  const sources = asArray(state.sources).flatMap((row) => {
    const parsed = parseSource(row);
    return parsed ? [parsed] : [];
  });
  const levels = asArray(state.levels).flatMap((row) => {
    const parsed = parseLevel(row);
    return parsed ? [parsed] : [];
  });
  const workTypes = asArray(state.workTypes).flatMap((row) => {
    const parsed = parseWorkType(row);
    return parsed ? [parsed] : [];
  });
  const cells = asArray(state.cells).flatMap((row) => {
    const parsed = parseCell(row);
    return parsed ? [parsed] : [];
  });
  const targets = asArray(state.targets).flatMap((row) => {
    const parsed = parseTarget(row);
    return parsed ? [parsed] : [];
  });
  const gaps = asArray(state.gaps).flatMap((row) => {
    const parsed = parseGap(row);
    return parsed ? [parsed] : [];
  });
  const structuralRows = isRecord(state.structural)
    ? asArray(state.structural.byLevel).flatMap((row) => {
      const parsed = parseStructural(row);
      return parsed ? [parsed] : [];
    })
    : [];
  const defectRegister = asArray(state.defectRegister).flatMap((row) => {
    const parsed = parseDefect(row);
    return parsed ? [parsed] : [];
  });
  const notes = asArray(state.notes).flatMap((row) => (typeof row === 'string' ? [row] : []));
  const warnings = asArray(state.registerAdapterWarnings).flatMap((row) =>
    typeof row === 'string' ? [row] : [],
  );
  const stages = asArray(state.stages).flatMap((row) => (typeof row === 'string' ? [row] : []));

  return {
    schema: 'programme-state/1',
    generatedAt: asString(state.generatedAt) ?? '',
    generatedBy: asString(state.generatedBy) ?? '',
    sources,
    levels,
    workTypes,
    stages,
    cells,
    targets,
    gaps,
    structural: { byLevel: structuralRows },
    draftingRuns: asArray(state.draftingRuns),
    defectRegister,
    notes,
    controls: parseControls(state.controls),
    registerAdapterWarnings: warnings,
    unclassifiedBacklog: asNullableInt(state.unclassifiedBacklog) ?? null,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function asBoolean(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

function asFiniteNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function asNullableInt(value: unknown): number | null | undefined {
  if (value === null) {
    return null;
  }
  if (typeof value === 'number' && Number.isInteger(value)) {
    return value;
  }
  return undefined;
}

function asLevel(value: unknown): LevelId | undefined {
  return value === 'I' || value === 'II' || value === 'III' ? value : undefined;
}

function parseSource(value: unknown): ProgrammeStateSource | null {
  if (!isRecord(value)) {
    return null;
  }
  const name = asString(value.name);
  const path = asString(value.path);
  const status = value.status === 'read' || value.status === 'unread' ? value.status : null;
  if (!name || !path || !status) {
    return null;
  }
  return {
    name,
    path,
    status,
    measuredAt: asString(value.measuredAt),
    digest: asString(value.digest),
    reason: asString(value.reason),
    complete: asBoolean(value.complete),
  };
}

function parseLevel(value: unknown): ProgrammeStateDocument['levels'][number] | null {
  if (!isRecord(value)) {
    return null;
  }
  const level = asLevel(value.level);
  const authoringCourseKey = asString(value.authoringCourseKey);
  const label = asString(value.label);
  if (!level || !authoringCourseKey || !label) {
    return null;
  }
  return {
    level,
    authoringCourseKey,
    servingCourseKeys: asArray(value.servingCourseKeys).flatMap((row) =>
      typeof row === 'string' ? [row] : [],
    ),
    label,
  };
}

function parseWorkType(value: unknown): ProgrammeStateWorkType | null {
  if (!isRecord(value)) {
    return null;
  }
  const id = asString(value.id);
  const label = asString(value.label);
  const description = asString(value.description) ?? '';
  const origin =
    value.origin === 'new' || value.origin === 'modify' || value.origin === 'move' || value.origin === 'none'
      ? value.origin
      : 'none';
  const contentKind = CONTENT_KIND_SET.has(String(value.contentKind))
    ? (value.contentKind as ContentKind)
    : null;
  const operation = OPERATION_SET.has(String(value.operation))
    ? (value.operation as Operation)
    : null;
  if (!id || !label || !contentKind || !operation) {
    return null;
  }
  return { id, label, description, origin, contentKind, operation };
}

function parseCell(value: unknown): ProgrammeStateCell | null {
  if (!isRecord(value)) {
    return null;
  }
  const level = asLevel(value.level);
  const workType = asString(value.workType);
  const stage = asString(value.stage);
  const count = asFiniteNumber(value.count);
  const measuredAt = asString(value.measuredAt);
  const source = asString(value.source);
  const unit = UNIT_SET.has(String(value.unit)) ? (value.unit as ProgrammeCellUnit) : null;
  if (!level || !workType || !stage || count === undefined || !measuredAt || !source || !unit) {
    return null;
  }
  if (!Number.isInteger(count) || count < 0) {
    return null;
  }
  const section = value.section === null || typeof value.section === 'string' ? value.section : null;
  return {
    level,
    section,
    workType,
    stage,
    count,
    measuredAt,
    source,
    unit,
    tentative: asBoolean(value.tentative),
    note: asString(value.note),
  };
}

function parseTarget(value: unknown): ProgrammeStateTarget | null {
  if (!isRecord(value)) {
    return null;
  }
  const level = asLevel(value.level);
  const workType = asString(value.workType);
  const basis = asString(value.basis);
  const measuredAt = asString(value.measuredAt);
  const source = asString(value.source);
  const floor = asNullableInt(value.floor);
  const full = asNullableInt(value.full);
  if (!level || !workType || !basis || !measuredAt || !source || floor === undefined || full === undefined) {
    return null;
  }
  return {
    level,
    section: value.section === null || typeof value.section === 'string' ? value.section : undefined,
    workType,
    floor,
    full,
    basis,
    measuredAt,
    source,
    unmappedRequirements: asNullableInt(value.unmappedRequirements),
    unit: asString(value.unit),
  };
}

function parseGap(value: unknown): ProgrammeStateGap | null {
  if (!isRecord(value)) {
    return null;
  }
  const level = asLevel(value.level);
  const workType = asString(value.workType);
  const basis = asString(value.basis);
  const measuredAt = asString(value.measuredAt);
  const source = asString(value.source);
  const remaining = asNullableInt(value.remaining);
  if (!level || !workType || !basis || !measuredAt || !source || remaining === undefined) {
    return null;
  }
  return {
    level,
    section: value.section === null || typeof value.section === 'string' ? value.section : undefined,
    workType,
    remaining,
    tentativeDiscard: asNullableInt(value.tentativeDiscard),
    note: asString(value.note),
    measuredAt,
    source,
    basis,
    inputs: asArray(value.inputs).flatMap((row) => (typeof row === 'string' ? [row] : [])),
    priority: asNullableInt(value.priority),
    discardCandidates: asNullableInt(value.discardCandidates),
    discarded: asNullableInt(value.discarded),
    reason: asString(value.reason),
    unit: asString(value.unit),
  };
}

function parseStructural(value: unknown): ProgrammeStateStructuralRow | null {
  if (!isRecord(value)) {
    return null;
  }
  const level = asLevel(value.level);
  const workType = asString(value.workType);
  const proposed = asNullableInt(value.proposed);
  const approved = asNullableInt(value.approved);
  const executed = asNullableInt(value.executed);
  const measuredAt = asString(value.measuredAt);
  const source = asString(value.source);
  if (
    !level
    || !workType
    || proposed === undefined
    || approved === undefined
    || executed === undefined
    || !measuredAt
    || !source
  ) {
    return null;
  }
  return { level, workType, proposed, approved, executed, measuredAt, source };
}

function parseDefect(value: unknown): ProgrammeStateDefect | null {
  if (!isRecord(value)) {
    return null;
  }
  const classId = asString(value.classId);
  const name = asString(value.name);
  const exposedBy = asExposedBy(value.exposedBy);
  const dimension = asDimension(value.dimension);
  const observation = value.observation === 'exposed' || value.observation === 'anticipated'
    ? value.observation
    : null;
  const disposition =
    value.disposition === 'process-fix'
    || value.disposition === 'redundancy-handled'
    || value.disposition === 'open'
      ? value.disposition
      : null;
  // `remediation` may legitimately be EMPTY and must never drop the row. Most classes in
  // `defect-classes.jsonl` record no built gate, and one (DC-13) carries no `process_change` at all;
  // the publisher writes '' rather than inventing a sentence. Treating empty as invalid silently
  // dropped 16 of the 20 published classes from the register — the view rendered four and said
  // nothing about the rest. Found by `gpt-6-astra` review, 2026-09-10.
  const remediation = typeof value.remediation === 'string' ? value.remediation : null;
  if (!classId || !name || !exposedBy || !dimension || !observation || !disposition || remediation === null) {
    return null;
  }
  if (!isRecord(value.detection) || !isRecord(value.verification)) {
    return null;
  }
  // Same for `detection.gate`: an empty gate is the true statement "no built check is recorded for
  // this class", which is the case for 16 of the 20 classes today. It is not a malformed row.
  const gate = typeof value.detection.gate === 'string' ? value.detection.gate : null;
  const detectionStatus = asDetectionStatus(value.detection.status);
  const verificationKind = asVerificationKind(value.verification.kind);
  if (gate === null || !detectionStatus || !verificationKind) {
    return null;
  }
  const firstSeen = value.firstSeen === null || typeof value.firstSeen === 'string' ? value.firstSeen : null;
  const on = value.verification.on === null || typeof value.verification.on === 'string'
    ? value.verification.on
    : null;
  return {
    classId,
    name,
    firstSeen,
    lastSeen: value.lastSeen === null || typeof value.lastSeen === 'string' ? value.lastSeen : undefined,
    occurrences: asNullableInt(value.occurrences) ?? null,
    exposedBy,
    dimension,
    detection: { gate, status: detectionStatus },
    remediation,
    disposition,
    description: asString(value.description),
    observation,
    definitionVersion: value.definitionVersion === null || typeof value.definitionVersion === 'string'
      ? value.definitionVersion
      : undefined,
    evidence: asArray(value.evidence).flatMap((row) => (typeof row === 'string' ? [row] : [])),
    verification: {
      kind: verificationKind,
      on,
      note: asString(value.verification.note),
    },
  };
}

function parseControls(value: unknown): ProgrammeStateControls | null {
  if (!isRecord(value)) {
    return null;
  }
  const decided = asFiniteNumber(value.decided);
  const caught = asFiniteNumber(value.caught);
  const unresolved = asFiniteNumber(value.unresolved);
  const source = asString(value.source);
  const measuredAt = asString(value.measuredAt);
  if (
    decided === undefined
    || caught === undefined
    || unresolved === undefined
    || !source
    || !measuredAt
  ) {
    return null;
  }
  return {
    decided,
    caught,
    unresolved,
    classesCovered: asArray(value.classesCovered).flatMap((row) => (typeof row === 'string' ? [row] : [])),
    note: asString(value.note),
    source,
    measuredAt,
  };
}

function asExposedBy(value: unknown): ProgrammeStateDefect['exposedBy'] | null {
  return value === 'scott' || value === 'gate' || value === 'preexisting' || value === 'mixed' || value === 'unknown'
    ? value
    : null;
}

function asDimension(value: unknown): ProgrammeStateDefect['dimension'] | null {
  return value === 'academic'
    || value === 'style'
    || value === 'verbosity'
    || value === 'structure'
    || value === 'process'
    || value === 'unknown'
    ? value
    : null;
}

function asDetectionStatus(value: unknown): ProgrammeStateDefect['detection']['status'] | null {
  return value === 'mechanical' || value === 'model' || value === 'none' || value === 'unknown' ? value : null;
}

function asVerificationKind(value: unknown): DefectVerificationKind | null {
  return value === 'rate_remeasured' || value === 'none' || value === 'dated_unverified' ? value : null;
}

export function levelsFromOverview(overview: ProgrammeOverview, filter: ProgrammeLevelFilter): LevelId[] {
  const fromCatalog = overview.levels.map((row) => row.level);
  const fromCells = unique(overview.cells.map((cell) => cell.level));
  const fromAutonomy = unique(overview.autonomy.map((row) => row.level));
  const available = unique([...fromCatalog, ...fromCells, ...fromAutonomy]);
  if (filter !== 'all') {
    return [filter];
  }
  return (['I', 'II', 'III'] as const).filter((level) => available.includes(level) || available.length === 0);
}
